import React, { useState } from 'react';
import * as Icons from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from './ui/dialog';
import api from '../api/client';
import { activitiesApi } from '../api/endpoints';
import { toast } from 'sonner';

/**
 * RecordingDialog
 * - Lets user upload an MP3/WAV/M4A recording for a lead's call
 *   AND/OR paste a URL (e.g. from Twilio/Exotel once integrated).
 * - Plays back any existing recordings on the lead (pulled from activities).
 */
const RecordingDialog = ({ trigger, lead, recordings = [], onChanged }) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [outcome, setOutcome] = useState('Interested');
  const [duration, setDuration] = useState('');
  const [busy, setBusy] = useState(false);

  const apiBase = (process.env.REACT_APP_BACKEND_URL || '');

  const resolveUrl = (u) => (u && u.startsWith('/api') ? `${apiBase}${u}` : u);

  const uploadFile = async (file) => {
    if (!file) return null;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('booking_id', lead.id);   // reuse the upload endpoint; booking_id field accepts any id-string
      fd.append('category', 'Finance');   // dummy category — backend stores it; UI reads via activities
      fd.append('doc_type', 'Call Recording');
      const r = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return r.data.file_url;
    } finally { setBusy(false); }
  };

  const save = async ({ recording_url }) => {
    setBusy(true);
    try {
      await activitiesApi.add(lead.id, {
        type: 'call',
        text: `Call recording attached${outcome ? ` · outcome: ${outcome}` : ''}${duration ? ` · ${duration}` : ''}`,
        recording_url,
        metadata: { outcome, duration, source: recording_url?.startsWith('/api/files') ? 'upload' : 'external' },
      });
      toast.success('Recording saved');
      setUrl('');
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save recording');
    } finally { setBusy(false); }
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/audio\//.test(f.type) && !/\.(mp3|wav|m4a|aac|ogg)$/i.test(f.name)) {
      toast.error('Only audio files (mp3/wav/m4a) supported');
      return;
    }
    try {
      const u = await uploadFile(f);
      if (u) await save({ recording_url: u });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    }
  };

  const onSaveUrl = async () => {
    if (!url.trim()) { toast.error('Enter a recording URL or upload a file'); return; }
    await save({ recording_url: url.trim() });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-xl" data-testid="recording-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Call Recordings · {lead?.displayId || lead?.id}</DialogTitle>
          <DialogDescription>
            Attach an MP3/WAV recording of your conversation with <b>{lead?.name}</b>. Either upload a file or paste a URL (e.g. from your telephony provider).
          </DialogDescription>
        </DialogHeader>

        {recordings.length > 0 && (
          <div className="space-y-2 mt-2 max-h-48 overflow-y-auto" data-testid="recordings-list">
            <div className="text-xs uppercase font-bold text-slate-500 tracking-wider">Existing recordings ({recordings.length})</div>
            {recordings.map((r) => {
              const src = resolveUrl(r.metadata?.recording_url);
              return (
                <div key={r.id} className="border rounded p-2">
                  <div className="text-[11px] text-slate-500 flex items-center justify-between">
                    <span>{new Date(r.createdAt).toLocaleString()} · {r.createdBy}</span>
                    {src && (
                      <a href={src} target="_blank" rel="noreferrer" className="text-brand-navy hover:underline">
                        <Icons.Download className="w-3.5 h-3.5 inline" />
                      </a>
                    )}
                  </div>
                  <div className="text-xs text-slate-700 mb-1">{r.text}</div>
                  {src && <audio controls src={src} className="w-full h-8" data-testid={`audio-${r.id}`} />}
                </div>
              );
            })}
          </div>
        )}

        <div className="space-y-3 pt-3 border-t mt-2">
          <div className="text-xs uppercase font-bold text-slate-500 tracking-wider">Add new recording</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px]">Outcome</Label>
              <Input value={outcome} onChange={e => setOutcome(e.target.value)} placeholder="Interested / Not Reachable / Callback" />
            </div>
            <div>
              <Label className="text-[11px]">Duration</Label>
              <Input value={duration} onChange={e => setDuration(e.target.value)} placeholder="e.g. 3m 12s" />
            </div>
          </div>
          <div>
            <Label className="text-[11px]">Upload audio (MP3/WAV/M4A)</Label>
            <input
              type="file" accept="audio/*,.mp3,.wav,.m4a"
              onChange={onPickFile}
              data-testid="recording-upload"
              className="block w-full text-sm border border-slate-200 rounded-md px-3 py-2 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-brand-navy file:text-white"
            />
          </div>
          <div>
            <Label className="text-[11px]">…or paste a URL</Label>
            <div className="flex gap-2">
              <Input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://example.com/recording.mp3" data-testid="recording-url" />
              <Button onClick={onSaveUrl} disabled={busy || !url.trim()} className="bg-brand-navy">Save URL</Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RecordingDialog;
